/**
 * First-challenge tour store (session-only, zustand). SEPARATE from guestWalkthroughStore so the
 * create flow's auto-dismiss of the legacy walkthrough can never kill this tour, and so we don't
 * disturb the guest-explore / WC-bridge triggers.
 *
 * The tour's visible step is DERIVED (see firstChallengeTourStep.helper) by the controller, which
 * writes `step` + `targetRect` here; the create flow publishes `createStep`/`pickCount`/`sent` via
 * `setCreateState`. See specs/first-challenge-onboarding/spec.md.
 */

import { create } from 'zustand';
import type { TargetRect } from '../../create/types/guestWalkthrough.types';
import type { FcTourStep } from '../constants/firstChallengeTour.constants';
import type { FcCreateStep } from '../helpers/firstChallengeTourStep.helper';

interface FirstChallengeTourState {
  active: boolean;
  skipped: boolean;
  /** Derived step (set by the controller). */
  step: FcTourStep | null;
  /** Beat index within a multi-beat screen (review / arena-live). */
  beat: number;
  /** Measured rect of the current spotlight target (set by useFirstChallengeTarget). */
  targetRect: TargetRect | null;
  /** Queued before navigating to Arena; consumed by the controller on the Arena route. */
  pendingStart: boolean;
  // Published from the create flow while the tour is active:
  createStep: FcCreateStep;
  pickCount: number;
  /** Latches true once the challenge is sent; stays true across the create → arena hand-off. */
  sent: boolean;

  setPendingStart: (v: boolean) => void;
  startTour: () => void;
  setStep: (step: FcTourStep | null) => void;
  setBeat: (n: number) => void;
  advanceBeat: () => void;
  setTargetRect: (rect: TargetRect | null) => void;
  setCreateState: (s: { createStep: FcCreateStep; pickCount: number }) => void;
  markSent: () => void;
  skip: () => void;
  resume: () => void;
  dismiss: () => void;
}

const INITIAL = {
  active: false,
  skipped: false,
  step: null as FcTourStep | null,
  beat: 0,
  targetRect: null as TargetRect | null,
  createStep: null as FcCreateStep,
  pickCount: 0,
  sent: false,
};

export const useFirstChallengeTourStore = create<FirstChallengeTourState>()((set, get) => ({
  ...INITIAL,
  pendingStart: false,

  setPendingStart: (v) => set({ pendingStart: v }),

  startTour: () => set({ ...INITIAL, active: true, pendingStart: false }),

  setStep: (step) => set({ step }),

  setBeat: (n) => set({ beat: Math.max(0, n) }),

  advanceBeat: () => set({ beat: get().beat + 1 }),

  setTargetRect: (rect) => set({ targetRect: rect }),

  setCreateState: ({ createStep, pickCount }) => set({ createStep, pickCount }),

  markSent: () => set({ sent: true }),

  skip: () => set({ skipped: true, targetRect: null }),

  resume: () => set({ skipped: false }),

  dismiss: () => set({ ...INITIAL, active: false, pendingStart: false }),
}));
